import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { io, Socket } from "socket.io-client";
import { useAuth } from "../context/AuthContext";
import TurndownService from "turndown";
// @ts-ignore - no types available for turndown-plugin-gfm
import { gfm } from "turndown-plugin-gfm";
import { marked } from "marked";

interface Note {
  id: string;
  content: string;
  title?: string;
  created_at: string;
  updated_at: string;
}

type ViewMode = "markdown" | "formatted" | "split";

const AUTOSAVE_DELAY_MS = 500;

// Generate a consistent color for a user based on their ID
const generateUserColor = (userId: string): string => {
  const colors = [
    "#FF6B6B", // red
    "#4ECDC4", // teal
    "#45B7D1", // blue
    "#FFA07A", // light salmon
    "#98D8C8", // mint
    "#F7DC6F", // yellow
    "#BB8FCE", // purple
    "#85C1E2", // sky blue
    "#F8B739", // orange
    "#52B788", // green
  ];

  // Generate hash from userId
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = userId.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
};

// Simple conflict resolution: merge changes intelligently
const mergeContent = (
  localContent: string,
  remoteContent: string,
  baseContent: string,
  cursorPos: number
): { merged: string; newCursorPos: number } => {
  // If local hasn't changed from base, accept remote
  if (localContent === baseContent) {
    return { merged: remoteContent, newCursorPos: cursorPos };
  }

  // If remote hasn't changed from base, keep local
  if (remoteContent === baseContent) {
    return { merged: localContent, newCursorPos: cursorPos };
  }

  // Both changed - try to merge intelligently
  // Find the common prefix and suffix
  let prefixEnd = 0;
  const minLen = Math.min(localContent.length, remoteContent.length);

  while (
    prefixEnd < minLen &&
    localContent[prefixEnd] === remoteContent[prefixEnd]
  ) {
    prefixEnd++;
  }

  let suffixStart1 = localContent.length;
  let suffixStart2 = remoteContent.length;

  while (
    suffixStart1 > prefixEnd &&
    suffixStart2 > prefixEnd &&
    localContent[suffixStart1 - 1] === remoteContent[suffixStart2 - 1]
  ) {
    suffixStart1--;
    suffixStart2--;
  }

  // If changes don't overlap, merge them
  const localChange = localContent.substring(prefixEnd, suffixStart1);
  const remoteChange = remoteContent.substring(prefixEnd, suffixStart2);

  // Simple strategy: if cursor is in the changed area, prefer local, otherwise prefer remote
  if (cursorPos >= prefixEnd && cursorPos <= suffixStart1) {
    // User is actively editing this area, keep local
    return { merged: localContent, newCursorPos: cursorPos };
  } else {
    // User is editing elsewhere, accept remote change
    const adjustment = remoteChange.length - localChange.length;
    const newCursorPos =
      cursorPos > prefixEnd ? cursorPos + adjustment : cursorPos;
    return { merged: remoteContent, newCursorPos };
  }
};

const NotesPage: React.FC = () => {
  const { note_id } = useParams<{ note_id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAuthenticated = !!user;

  const [note, setNote] = useState<Note | null>(null);
  const [notes, setNotes] = useState<Note[]>([]);
  const [content, setContent] = useState("");
  const [lastServerContent, setLastServerContent] = useState(""); // Track server state
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notesError, setNotesError] = useState<string | null>(null);
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const [hasLoadedInitialNotes, setHasLoadedInitialNotes] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [wsConnected, setWsConnected] = useState(false); // Track WebSocket connection status
  const [viewMode, setViewMode] = useState<ViewMode>("formatted"); // View mode for Obsidian-like experience
  const [showMarkdownHelp, setShowMarkdownHelp] = useState(false); // Toggle markdown help overlay
  const [remoteCursors, setRemoteCursors] = useState<
    Map<
      string,
      {
        position: number;
        view_mode: "markdown" | "formatted";
        user_name: string;
        color: string;
      }
    >
  >(new Map()); // Track other users' cursors
  const [scrollOffset, setScrollOffset] = useState({ top: 0, left: 0 }); // Track textarea scroll position

  // Track textarea scroll position for cursor positioning
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const handleScroll = () => {
      setScrollOffset({
        top: textarea.scrollTop,
        left: textarea.scrollLeft,
      });
    };

    textarea.addEventListener("scroll", handleScroll);
    return () => textarea.removeEventListener("scroll", handleScroll);
  }, []);

  // Refs for formatted view
  const formattedViewRef = useRef<HTMLDivElement>(null);
  const isFormattedEditingRef = useRef(false);
  const [formattedHTML, setFormattedHTML] = useState("");
  const lastContentRef = useRef("");
  const [formattedKey, setFormattedKey] = useState(0); // Key to force re-render
  const lastFormattedActionRef = useRef<string>(""); // Track last action for double-enter list exit

  // Initialize Turndown for HTML to Markdown conversion
  const turndownService = useRef<TurndownService | null>(null);

  useEffect(() => {
    // Configure marked for markdown to HTML with custom checkbox rendering
    const renderer = new marked.Renderer();

    // Override listitem to handle task lists properly
    const originalListitem = renderer.listitem.bind(renderer);
    renderer.listitem = function (this: any, item: any) {
      if (item.task) {
        // Clean the text to remove any checkbox syntax that might be included
        let cleanText = item.text;
        // Remove leading [ ] or [x] if present
        cleanText = cleanText.replace(/^\s*\[[ x]\]\s*/i, "");

        const checkboxHtml = `<input type="checkbox" ${
          item.checked ? "checked" : ""
        } />`;
        // Wrap text in span to match programmatically created structure and ensure proper cursor positioning
        return `<li class="task-list-item">${checkboxHtml}<span>${cleanText}</span></li>\n`;
      }
      return originalListitem.call(this, item);
    };

    marked.setOptions({
      gfm: true,
      breaks: true,
      renderer: renderer,
    });

    // Configure turndown for HTML to markdown
    const service = new TurndownService({
      headingStyle: "atx",
      codeBlockStyle: "fenced",
      bulletListMarker: "-",
    });
    service.use(gfm);

    // Custom rule for checkboxes
    service.addRule("taskListItems", {
      filter: (node) => {
        return (
          node.nodeName === "LI" && node.classList.contains("task-list-item")
        );
      },
      replacement: (_content, node: any) => {
        const checkbox = node.querySelector('input[type="checkbox"]');
        const checked = checkbox?.checked || false;

        // Get text content, excluding the checkbox element
        const clonedNode = node.cloneNode(true) as HTMLElement;
        const clonedCheckbox = clonedNode.querySelector(
          'input[type="checkbox"]'
        );
        if (clonedCheckbox) {
          clonedCheckbox.remove();
        }
        const cleanContent = clonedNode.textContent?.trim() || "";

        return `- [${checked ? "x" : " "}] ${cleanContent}\n`;
      },
    });

    turndownService.current = service;
  }, []);

  // Update formatted HTML when markdown content changes (but not while editing formatted view)
  useEffect(() => {
    if (!isFormattedEditingRef.current && content !== lastContentRef.current) {
      const html = marked(content) as string;

      setFormattedHTML(html);
      lastContentRef.current = content;
      setFormattedKey((prev) => prev + 1);
    }
  }, [content]);

  // Sync formatted HTML when switching to a view that shows the formatted pane
  // This handles the case where edits were made in formatted view, then user switches views
  useEffect(() => {
    if (viewMode === "formatted" || viewMode === "split") {
      // Always regenerate formatted HTML from current content when showing formatted pane
      const html = marked(content) as string;
      setFormattedHTML(html);
      lastContentRef.current = content;
      setFormattedKey((prev) => prev + 1);
    }
  }, [viewMode]);

  // Undo/Redo state
  const [history, setHistory] = useState<string[]>([""]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const isUndoRedoRef = useRef(false);

  // WebSocket refs
  const socketRef = useRef<Socket | null>(null);
  const isReceivingUpdateRef = useRef(false); // Flag to prevent echo
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const lastMarkdownActionRef = useRef<string>(""); // Track last action for double-enter list exit

  const loadNotes = async (showLoading = true) => {
    // Don't load notes list if not authenticated
    if (!isAuthenticated) {
      return;
    }

    try {
      if (showLoading) {
        setIsLoadingNotes(true);
      }
      setNotesError(null);
      const res = await fetch("/api/notes", {
        credentials: "include",
      });
      if (!res.ok) {
        throw new Error("Failed to load notes");
      }
      const data = await res.json();
      const notesList = Array.isArray(data.notes) ? data.notes : [];
      // Sort by created_at, newest first
      notesList.sort((a: Note, b: Note) => {
        return (
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      });
      setNotes(notesList);
      setHasLoadedInitialNotes(true);
    } catch (e: any) {
      setNotesError(e.message || "Could not load notes");
      setHasLoadedInitialNotes(true);
    } finally {
      setIsLoadingNotes(false);
    }
  };

  // Initial load of notes list for sidebar (only if authenticated).
  useEffect(() => {
    if (isAuthenticated) {
      loadNotes();
    }
  }, [isAuthenticated]);

  // Poll the notes list every 10 seconds to pick up background title updates (only if authenticated).
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const interval = setInterval(() => {
      loadNotes(false);
    }, 10000); // 10 seconds

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Initialize WebSocket connection (once on mount)
  useEffect(() => {
    // In development, explicitly connect to the backend server
    // In production, connect to same origin
    const isDev =
      window.location.port !== "8000" && window.location.port !== "";
    const socketUrl = isDev ? "http://localhost:8000" : window.location.origin;

    // Connect to the backend WebSocket server
    const socket = io(socketUrl, {
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      timeout: 10000,
      path: "/socket.io/",
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      setWsConnected(true);
    });

    socket.on("disconnect", () => {
      setWsConnected(false);
    });

    socket.on("user_left", (data) => {
      // Remove their cursor
      setRemoteCursors((prev) => {
        const updated = new Map(prev);
        updated.delete(data.user_id);
        return updated;
      });
    });

    // Handle cursor position updates from other users
    socket.on("cursor_update", (data) => {
      setRemoteCursors((prev) => {
        const updated = new Map(prev);
        // Assign a consistent color based on user_id
        const existingCursor = prev.get(data.user_id);
        const color = existingCursor?.color || generateUserColor(data.user_id);

        updated.set(data.user_id, {
          position: data.position,
          view_mode: data.view_mode || "markdown",
          user_name: data.user_name || "Anonymous",
          color: color,
        });
        return updated;
      });
    });

    return () => {
      socket.disconnect();
    };
  }, []); // Only run once on mount

  // Handle incoming note updates (needs to be separate to access current state)
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;

    const handleNoteUpdate = (data: any) => {
      // Only apply updates if they're for the current note
      if (data.note_id === note_id) {
        isReceivingUpdateRef.current = true;

        // Store cursor position before update
        const textarea = textareaRef.current;
        const cursorPos = textarea?.selectionStart || 0;

        // Apply conflict resolution
        setContent((currentContent) => {
          const { merged, newCursorPos } = mergeContent(
            currentContent,
            data.content,
            lastServerContent,
            cursorPos
          );

          // Update base for next merge
          setLastServerContent(data.content);

          // Restore cursor position after React updates the DOM
          setTimeout(() => {
            if (textarea) {
              textarea.selectionStart = newCursorPos;
              textarea.selectionEnd = newCursorPos;
            }
            isReceivingUpdateRef.current = false;
          }, 0);

          return merged;
        });
      }
    };

    socket.on("note_updated", handleNoteUpdate);

    return () => {
      socket.off("note_updated", handleNoteUpdate);
    };
  }, [note_id, lastServerContent]);

  // Join/leave note rooms when note_id changes
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !note_id) return;

    // Leave previous room if any
    if (note?.id && note.id !== note_id) {
      socket.emit("leave_note", { note_id: note.id });
    }

    // Join new room
    socket.emit("join_note", { note_id });
    setRemoteCursors(new Map()); // Clear remote cursors when switching notes

    return () => {
      socket.emit("leave_note", { note_id });
      setRemoteCursors(new Map()); // Clear cursors on cleanup
    };
  }, [note_id, note?.id]);

  // When there's no note_id in the URL, redirect to the most recent note (or create if none exist).
  useEffect(() => {
    // Wait for initial notes load to complete
    if (!note_id && hasLoadedInitialNotes) {
      if (notes.length > 0) {
        // Redirect to the most recent note (first in the sorted list)
        navigate(`/notes/${notes[0].id}`, { replace: true });
      } else {
        // Only create a new note if there are no notes
        const create = async () => {
          try {
            const res = await fetch("/api/notes", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({ content: "" }),
            });

            if (!res.ok) {
              throw new Error("Failed to create note");
            }

            const data: Note = await res.json();
            navigate(`/notes/${data.id}`, { replace: true });
          } catch (e: any) {
            setError(e.message || "Could not create note");
          }
        };
        create();
      }
    }
  }, [note_id, navigate, notes, hasLoadedInitialNotes]);

  // Load existing note when note_id changes.
  useEffect(() => {
    if (!note_id) return;

    const load = async () => {
      try {
        setError(null);
        const res = await fetch(`/api/notes/${note_id}`, {
          credentials: "include",
        });
        if (!res.ok) {
          if (res.status === 404) {
            setError("Note not found");
            return;
          }
          throw new Error("Failed to load note");
        }
        const data: Note = await res.json();
        setNote(data);
        setContent(data.content || "");
        setLastServerContent(data.content || ""); // Track what the server has
        setLastSaved(data.updated_at);
        // Update the note in the sidebar list without reordering
        setNotes((prev) => {
          const existingIndex = prev.findIndex((n) => n.id === data.id);
          if (existingIndex >= 0) {
            // Update in place
            const updated = [...prev];
            updated[existingIndex] = data;
            return updated;
          } else {
            // New note, add to top
            return [data, ...prev];
          }
        });
      } catch (e: any) {
        setError(e.message || "Could not load note");
      }
    };

    load();
  }, [note_id]);

  // Broadcast changes immediately via WebSocket (no debounce for real-time sync)
  useEffect(() => {
    if (!note_id) return;

    // Skip broadcasting if we're receiving an update from someone else
    if (isReceivingUpdateRef.current) {
      return;
    }

    // Skip if content hasn't changed from server
    if (content === lastServerContent) {
      return;
    }

    // Broadcast changes to other connected clients via WebSocket IMMEDIATELY
    const socket = socketRef.current;
    if (socket && socket.connected) {
      socket.emit("note_update", {
        note_id,
        content,
        cursor_position: textareaRef.current?.selectionStart,
      });
    } else if (socket && !socket.connected) {
      // Try to reconnect if socket exists but isn't connected
      socket.connect();
    }
  }, [content, note_id, lastServerContent]);

  // Broadcast cursor position when user moves cursor (throttled)
  const lastCursorBroadcastRef = useRef<number>(0);
  const handleCursorMove = useCallback(() => {
    if (!note_id || !textareaRef.current) return;

    const socket = socketRef.current;
    if (!socket?.connected) return;

    const now = Date.now();
    const timeSinceLastBroadcast = now - lastCursorBroadcastRef.current;

    // Throttle cursor updates to every 100ms
    if (timeSinceLastBroadcast < 100) return;

    lastCursorBroadcastRef.current = now;
    const position = textareaRef.current.selectionStart;
    const userName = user?.email?.split("@")[0] || "Anonymous";

    socket.emit("cursor_position", {
      note_id,
      position,
      view_mode: "markdown",
      user_name: userName,
    });
  }, [note_id, user]);

  // Handle cursor movement in formatted view
  const handleFormattedCursorMove = useCallback(() => {
    if (!note_id) return;

    const socket = socketRef.current;
    if (!socket?.connected) return;

    const now = Date.now();
    const timeSinceLastBroadcast = now - lastCursorBroadcastRef.current;

    // Throttle cursor updates to every 100ms
    if (timeSinceLastBroadcast < 100) return;

    lastCursorBroadcastRef.current = now;

    // For formatted view, get the visible character position directly
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    if (formattedViewRef.current) {
      preCaretRange.selectNodeContents(formattedViewRef.current);
      preCaretRange.setEnd(range.endContainer, range.endOffset);
      const position = preCaretRange.toString().length;

      const userName = user?.email?.split("@")[0] || "Anonymous";

      socket.emit("cursor_position", {
        note_id,
        position,
        view_mode: "formatted",
        user_name: userName,
      });
    }
  }, [note_id, user]);

  // Debounced autosave to database (separate from WebSocket broadcast)
  useEffect(() => {
    if (!note_id) return;

    // Skip save if content hasn't changed from what the server has
    if (content === lastServerContent) {
      return;
    }

    const handle = setTimeout(async () => {
      try {
        setIsSaving(true);
        setError(null);
        const res = await fetch(`/api/notes/${note_id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ content }),
        });
        if (!res.ok) {
          throw new Error("Failed to save note");
        }
        const data: Note = await res.json();
        setNote(data);
        setLastServerContent(content); // Update server state tracker
        setLastSaved(data.updated_at);
        // Keep sidebar list in sync, newest note first
        setNotes((prev) => {
          const others = prev.filter((n) => n.id !== data.id);
          return [data, ...others];
        });
      } catch (e: any) {
        setError(e.message || "Could not save note");
      } finally {
        setIsSaving(false);
      }
    }, AUTOSAVE_DELAY_MS);

    return () => clearTimeout(handle);
  }, [content, note_id, lastServerContent]);

  const handleNewNote = async () => {
    try {
      const res = await fetch("/api/notes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ content: "" }),
      });
      if (!res.ok) {
        throw new Error("Failed to create note");
      }
      const data: Note = await res.json();
      setNotes((prev) => [data, ...prev.filter((n) => n.id !== data.id)]);
      navigate(`/notes/${data.id}`);
      setIsDrawerOpen(false); // Close drawer on mobile when navigating
    } catch (e: any) {
      setError(e.message || "Could not create note");
    }
  };

  const handleDeleteCurrent = async () => {
    if (!note_id) return;

    try {
      setIsDeleting(true);
      const res = await fetch(`/api/notes/${note_id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) {
        throw new Error("Failed to delete note");
      }

      // Remove from local sidebar list
      setNotes((prev) => prev.filter((n) => n.id !== note_id));

      // Decide where to go next
      const remaining = notes.filter((n) => n.id !== note_id);
      if (remaining.length > 0) {
        navigate(`/notes/${remaining[0].id}`);
      } else {
        navigate("/notes");
      }
      setShowDeleteModal(false);
    } catch (e: any) {
      setError(e.message || "Could not delete note");
    } finally {
      setIsDeleting(false);
    }
  };

  const shareUrl = window.location.href;

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const getTitle = (n: Note) => {
    if (n.title && n.title.trim().length > 0) {
      return n.title.trim();
    }
    return "Untitled";
  };

  const getShortTime = (iso: string) => {
    try {
      const date = new Date(iso);
      const dateStr = date.toLocaleDateString([], {
        month: "2-digit",
        day: "2-digit",
        year: "2-digit",
      });
      const timeStr = date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
      return `${dateStr} ${timeStr}`;
    } catch {
      return "";
    }
  };

  // Update history when content changes (for undo/redo)
  useEffect(() => {
    if (isUndoRedoRef.current) {
      isUndoRedoRef.current = false;
      return;
    }

    // Debounce history updates to avoid too many entries
    const timer = setTimeout(() => {
      setHistory((prev) => {
        const newHistory = prev.slice(0, historyIndex + 1);
        if (newHistory[newHistory.length - 1] !== content) {
          return [...newHistory, content];
        }
        return prev;
      });
      setHistoryIndex((prev) => {
        if (history[prev] !== content) {
          return prev + 1;
        }
        return prev;
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [content]);

  // Undo function
  const undo = () => {
    if (historyIndex > 0) {
      isUndoRedoRef.current = true;
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setContent(history[newIndex]);
    }
  };

  // Redo function
  const redo = () => {
    if (historyIndex < history.length - 1) {
      isUndoRedoRef.current = true;
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setContent(history[newIndex]);
    }
  };

  // Handle formatted view editing (contentEditable)
  const handleFormattedInput = () => {
    if (!turndownService.current || !formattedViewRef.current) return;

    try {
      // Before converting, update checkbox attributes to match their checked properties
      const checkboxes = formattedViewRef.current.querySelectorAll(
        'input[type="checkbox"]'
      );
      checkboxes.forEach((checkbox: any) => {
        if (checkbox.checked) {
          checkbox.setAttribute("checked", "checked");
        } else {
          checkbox.removeAttribute("checked");
        }
      });

      // Get the HTML content from the contentEditable div
      const html = formattedViewRef.current.innerHTML;

      // Convert HTML back to markdown
      const markdown = turndownService.current.turndown(html);

      // Update the content (this will trigger auto-save)
      lastContentRef.current = markdown;
      setContent(markdown);
    } catch (error) {
      console.error("Error converting HTML to markdown:", error);
    }
  };

  // Helper function to check if selection is in formatted view
  const isSelectionInFormattedView = useCallback(() => {
    const formattedDiv = formattedViewRef.current;
    if (!formattedDiv) return false;

    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return false;

    const range = selection.getRangeAt(0);
    const container = range.commonAncestorContainer;

    // Check if the selection is within the formatted view
    const element =
      container.nodeType === Node.ELEMENT_NODE
        ? container
        : container.parentElement;

    return element ? formattedDiv.contains(element) : false;
  }, []);

  // Helper function to apply formatting in formatted view
  const applyFormattingInFormattedView = useCallback((tag: string) => {
    const formattedDiv = formattedViewRef.current;
    if (!formattedDiv) return;

    // Make sure the div is focused
    formattedDiv.focus();

    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    const selectedText = range.toString();

    // Create the appropriate element
    const element = document.createElement(tag);

    if (selectedText) {
      // Wrap the selected text
      element.textContent = selectedText;
      range.deleteContents();
      range.insertNode(element);

      // Move cursor after the inserted element
      const newRange = document.createRange();
      newRange.setStartAfter(element);
      newRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(newRange);
    } else {
      // Insert element with placeholder text
      element.textContent =
        tag === "strong"
          ? "bold"
          : tag === "em"
          ? "italic"
          : tag === "code"
          ? "code"
          : "text";
      range.insertNode(element);

      // Select the placeholder text
      const newRange = document.createRange();
      newRange.selectNodeContents(element);
      selection.removeAllRanges();
      selection.addRange(newRange);
    }

    // Sync to markdown
    setTimeout(() => handleFormattedInput(), 10);
  }, []);

  // Helper function to insert heading in formatted view
  const insertHeadingInFormattedView = useCallback((level: number) => {
    const formattedDiv = formattedViewRef.current;
    if (!formattedDiv) return;

    // Make sure the div is focused
    formattedDiv.focus();

    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return;

    const range = selection.getRangeAt(0);

    // Create heading element
    const heading = document.createElement(`h${level}`);
    heading.textContent = "Heading";

    // Get current position
    const currentNode = range.startContainer;

    // Try to find the current block element to replace
    let blockNode: Node | null = currentNode;
    while (blockNode && blockNode !== formattedDiv) {
      if (blockNode.nodeType === Node.ELEMENT_NODE) {
        const nodeName = (blockNode as HTMLElement).nodeName;
        // Check if it's a block-level element
        if (
          ["P", "DIV", "H1", "H2", "H3", "H4", "H5", "H6", "LI"].includes(
            nodeName
          )
        ) {
          break;
        }
      }
      blockNode = blockNode.parentNode;
    }

    if (
      blockNode &&
      blockNode !== formattedDiv &&
      blockNode.nodeType === Node.ELEMENT_NODE
    ) {
      // Replace the current block with the heading
      (blockNode as HTMLElement).parentNode?.replaceChild(
        heading,
        blockNode as HTMLElement
      );
    } else {
      // Insert as new block
      range.deleteContents();
      range.insertNode(heading);

      // Add a line break after if at end
      const br = document.createElement("br");
      heading.parentNode?.insertBefore(br, heading.nextSibling);
    }

    // Select the heading text
    const newRange = document.createRange();
    newRange.selectNodeContents(heading);
    selection.removeAllRanges();
    selection.addRange(newRange);

    // Sync to markdown
    setTimeout(() => handleFormattedInput(), 10);
  }, []);

  // Handle keyboard events in formatted view
  const handleFormattedKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Handle Enter key for smart list continuation or new line
    if (e.key === "Enter" && !e.shiftKey) {
      const selection = window.getSelection();
      if (!selection || !selection.rangeCount) return;

      const range = selection.getRangeAt(0);
      let node: Node | null = range.startContainer;

      // Find the parent LI element
      while (node && node.nodeName !== "LI") {
        node = node.parentNode as Node;
        if (!node || node.nodeName === "BODY") break;
      }

      if (!node || node.nodeName !== "LI") {
        // Not in a list, allow default Enter behavior (insert new paragraph)
        // Don't prevent default - let browser handle it naturally
        return;
      }

      // We're in a list, prevent default and handle list continuation
      e.preventDefault();
      e.stopPropagation();

      const li = node as HTMLLIElement;

      // Check if it's a task list item
      const checkbox = li.querySelector('input[type="checkbox"]');

      if (checkbox) {
        // Get the text content (excluding the checkbox)
        const cloneLi = li.cloneNode(true) as HTMLLIElement;
        const cloneCheckbox = cloneLi.querySelector('input[type="checkbox"]');
        if (cloneCheckbox) {
          cloneCheckbox.remove();
        }
        let textContent = cloneLi.textContent?.trim() || "";
        // Remove zero-width spaces that we insert as placeholders
        textContent = textContent.replace(/[\u200B-\u200D\uFEFF]/g, "");

        // If empty (just checkbox), check if this is the second consecutive Enter
        if (textContent === "") {
          // Check if the last action was also creating an empty task item
          if (lastFormattedActionRef.current === "empty-task-item") {
            // Second Enter on empty item - exit the list
            const ul = li.parentElement;
            if (ul) {
              const newP = document.createElement("p");
              const br = document.createElement("br");
              newP.appendChild(br);

              if (ul.nextSibling) {
                ul.parentNode?.insertBefore(newP, ul.nextSibling);
              } else {
                ul.parentNode?.appendChild(newP);
              }

              li.remove();

              // Move cursor to new paragraph
              setTimeout(() => {
                const newRange = document.createRange();
                newRange.setStart(newP, 0);
                newRange.collapse(true);
                selection.removeAllRanges();
                selection.addRange(newRange);
              }, 0);

              // Reset the last action
              lastFormattedActionRef.current = "";

              // Sync to markdown
              setTimeout(() => handleFormattedInput(), 10);
            }
            return;
          }
          // First Enter on empty item - create another empty item and track it
          lastFormattedActionRef.current = "empty-task-item";
        } else {
          // Has text content, reset the tracking
          lastFormattedActionRef.current = "";
        }

        // Create new task list item - SIMPLE VERSION
        const newLi = document.createElement("li");
        newLi.className = "task-list-item";

        const newCheckbox = document.createElement("input");
        newCheckbox.type = "checkbox";
        newCheckbox.disabled = false;
        newCheckbox.addEventListener("click", handleCheckboxClick);

        // Just add a text node directly (no span wrapper)
        const textNode = document.createTextNode("\u200B"); // Zero-width space as placeholder

        newLi.appendChild(newCheckbox);
        newLi.appendChild(textNode);

        // Insert after current li
        li.parentNode?.insertBefore(newLi, li.nextSibling);

        // Place cursor in the text node
        const newRange = document.createRange();
        newRange.setStart(textNode, 1); // After the zero-width space
        newRange.collapse(true);

        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(newRange);

        // Sync to markdown without re-rendering
        isFormattedEditingRef.current = true;
        lastContentRef.current = ""; // Force update
        handleFormattedInput();
        setTimeout(() => {
          isFormattedEditingRef.current = false;
        }, 100);

        return;
      } else {
        // Regular list item (non-task)
        // Get the text content
        const textContent = li.textContent?.trim() || "";

        // If empty, check if this is the second consecutive Enter
        if (textContent === "") {
          // Check if the last action was also creating an empty list item
          if (lastFormattedActionRef.current === "empty-list-item") {
            // Second Enter on empty item - exit the list
            const ul = li.parentElement;
            if (ul) {
              const newP = document.createElement("p");
              const br = document.createElement("br");
              newP.appendChild(br);

              if (ul.nextSibling) {
                ul.parentNode?.insertBefore(newP, ul.nextSibling);
              } else {
                ul.parentNode?.appendChild(newP);
              }

              li.remove();

              // Move cursor to new paragraph
              setTimeout(() => {
                const newRange = document.createRange();
                newRange.setStart(newP, 0);
                newRange.collapse(true);
                selection.removeAllRanges();
                selection.addRange(newRange);
              }, 0);

              // Reset the last action
              lastFormattedActionRef.current = "";

              // Sync to markdown
              setTimeout(() => handleFormattedInput(), 10);
            }
            return;
          }
          // First Enter on empty item - create another empty item and track it
          lastFormattedActionRef.current = "empty-list-item";
        } else {
          // Has text content, reset the tracking
          lastFormattedActionRef.current = "";
        }

        // Create new regular list item
        const newLi = document.createElement("li");
        newLi.appendChild(document.createTextNode(""));

        // Insert after current li
        if (li.parentNode) {
          li.parentNode.insertBefore(newLi, li.nextSibling);

          // Move cursor to new item
          setTimeout(() => {
            const newRange = document.createRange();
            newRange.setStart(newLi, 0);
            newRange.collapse(true);
            selection.removeAllRanges();
            selection.addRange(newRange);
            formattedViewRef.current?.focus();
          }, 0);

          // Sync to markdown
          setTimeout(() => handleFormattedInput(), 10);
        }

        return;
      }
    }
  };

  // Handle checkbox clicks in formatted view
  const handleCheckboxClick = useCallback(() => {
    // Trigger an input event to convert HTML back to markdown
    // Use a small delay to ensure the checkbox state is updated before converting
    setTimeout(() => {
      handleFormattedInput();
    }, 10);
  }, []);

  // Add event listeners to checkboxes after content updates
  useEffect(() => {
    if (formattedViewRef.current) {
      const checkboxes = formattedViewRef.current.querySelectorAll(
        'input[type="checkbox"]'
      );
      checkboxes.forEach((checkbox) => {
        checkbox.addEventListener("click", handleCheckboxClick);
      });

      return () => {
        checkboxes.forEach((checkbox) => {
          checkbox.removeEventListener("click", handleCheckboxClick);
        });
      };
    }
  }, [formattedHTML, formattedKey, handleCheckboxClick]);

  // Auto-bullet feature: detect "- " pattern and convert to bullet list
  useEffect(() => {
    const formattedDiv = formattedViewRef.current;
    if (!formattedDiv) return;

    const handleBeforeInput = (e: InputEvent) => {
      // Check if we just typed a space after "- " at the start of a line
      if (e.inputType === "insertText" && e.data === " ") {
        const selection = window.getSelection();
        if (!selection || !selection.rangeCount) return;

        const range = selection.getRangeAt(0);
        const node = range.startContainer;

        // Check if we're in a paragraph (not already in a list)
        let currentNode: Node | null = node;
        let isParagraph = false;

        while (currentNode) {
          if (currentNode.nodeName === "P") {
            isParagraph = true;
            break;
          }
          if (
            currentNode.nodeName === "UL" ||
            currentNode.nodeName === "OL" ||
            currentNode.nodeName === "LI"
          ) {
            // Already in a list, don't convert
            return;
          }
          currentNode = currentNode.parentNode;
        }

        if (!isParagraph) return;

        // Get the text content up to the cursor
        if (node.nodeType === Node.TEXT_NODE) {
          const textContent = node.textContent || "";
          const cursorOffset = range.startOffset;
          const textBeforeCursor = textContent.substring(0, cursorOffset);

          // Check if the text before cursor is exactly "-"
          if (textBeforeCursor === "-") {
            e.preventDefault();

            // Get the paragraph element
            let paragraph: HTMLElement | null = node.parentElement;
            while (paragraph && paragraph.nodeName !== "P") {
              paragraph = paragraph.parentElement;
            }

            if (!paragraph) return;

            // Create a bullet list
            const ul = document.createElement("ul");
            const li = document.createElement("li");

            // Add empty text node for cursor placement
            li.appendChild(document.createTextNode(""));
            ul.appendChild(li);

            // Replace the paragraph with the list
            paragraph.parentNode?.replaceChild(ul, paragraph);

            // Set cursor inside the list item
            setTimeout(() => {
              const newRange = document.createRange();
              const newSelection = window.getSelection();
              if (li.firstChild) {
                newRange.setStart(li.firstChild, 0);
                newRange.collapse(true);
                newSelection?.removeAllRanges();
                newSelection?.addRange(newRange);
              }

              // Sync to markdown after a short delay
              setTimeout(() => handleFormattedInput(), 10);
            }, 0);
          }
        }
      }
    };

    formattedDiv.addEventListener("beforeinput", handleBeforeInput);

    return () => {
      formattedDiv.removeEventListener("beforeinput", handleBeforeInput);
    };
  }, [formattedHTML, formattedKey]);

  // Handle keyboard shortcuts
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Cmd/Ctrl + Z for undo
    if ((e.metaKey || e.ctrlKey) && e.key === "z" && !e.shiftKey) {
      e.preventDefault();
      undo();
      return;
    }

    // Cmd/Ctrl + Shift + Z for redo
    if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === "z") {
      e.preventDefault();
      redo();
      return;
    }

    // Cmd/Ctrl + Y for redo (alternative)
    if ((e.metaKey || e.ctrlKey) && e.key === "y") {
      e.preventDefault();
      redo();
      return;
    }

    // Enter key - smart list continuation
    if (e.key === "Enter") {
      handleCursorMove(); // Broadcast new cursor position after Enter
      const textarea = textareaRef.current;
      if (!textarea) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;

      // Find the current line
      const beforeCursor = content.substring(0, start);
      const afterCursor = content.substring(end);
      const lines = beforeCursor.split("\n");
      const currentLine = lines[lines.length - 1];

      // Check for task list FIRST (before bullet list, since task lists also have bullets)
      const taskMatch = currentLine.match(
        /^(\s*)([-*+])\s+\[([ x])\]\s+(.*)$/i
      );
      if (taskMatch) {
        const [, indent, bullet, , text] = taskMatch;

        // If the line is empty (just the checkbox), check if this is the second consecutive Enter
        if (text.trim() === "") {
          // Check if the last action was also creating an empty task item
          if (lastMarkdownActionRef.current === "empty-task-item") {
            // Second Enter on empty item - remove it and don't continue the list
            e.preventDefault();
            const newContent =
              beforeCursor.slice(0, -currentLine.length) + afterCursor;
            setContent(newContent);
            setTimeout(() => {
              textarea.selectionStart = start - currentLine.length;
              textarea.selectionEnd = start - currentLine.length;
            }, 0);
            // Reset the last action
            lastMarkdownActionRef.current = "";
            return;
          }
          // First Enter on empty item - create another empty item and track it
          lastMarkdownActionRef.current = "empty-task-item";
        } else {
          // Has text content, reset the tracking
          lastMarkdownActionRef.current = "";
        }

        // Continue the task list (unchecked)
        e.preventDefault();
        const newContent = `${beforeCursor}\n${indent}${bullet} [ ] ${afterCursor}`;
        setContent(newContent);
        setTimeout(() => {
          const newPos = start + indent.length + 7; // +7 for \n, bullet, space, [, ], space
          textarea.selectionStart = newPos;
          textarea.selectionEnd = newPos;
        }, 0);
        return;
      }

      // Check for numbered list
      const numberedMatch = currentLine.match(/^(\s*)(\d+)\.\s+(.*)$/);
      if (numberedMatch) {
        const [, indent, num, text] = numberedMatch;

        // If the line is empty (just the number), check if this is the second consecutive Enter
        if (text.trim() === "") {
          // Check if the last action was also creating an empty numbered item
          if (lastMarkdownActionRef.current === "empty-numbered-item") {
            // Second Enter on empty item - remove it and don't continue the list
            e.preventDefault();
            const newContent =
              beforeCursor.slice(0, -currentLine.length) + afterCursor;
            setContent(newContent);
            setTimeout(() => {
              textarea.selectionStart = start - currentLine.length;
              textarea.selectionEnd = start - currentLine.length;
            }, 0);
            // Reset the last action
            lastMarkdownActionRef.current = "";
            return;
          }
          // First Enter on empty item - create another empty item and track it
          lastMarkdownActionRef.current = "empty-numbered-item";
        } else {
          // Has text content, reset the tracking
          lastMarkdownActionRef.current = "";
        }

        // Continue the numbered list
        e.preventDefault();
        const nextNum = parseInt(num) + 1;
        const newContent = `${beforeCursor}\n${indent}${nextNum}. ${afterCursor}`;
        setContent(newContent);
        setTimeout(() => {
          const newPos = start + indent.length + nextNum.toString().length + 3; // +3 for \n, ., and space
          textarea.selectionStart = newPos;
          textarea.selectionEnd = newPos;
        }, 0);
        return;
      }

      // Check for bullet list (after task list check)
      const bulletMatch = currentLine.match(/^(\s*)([-*+])\s+(.*)$/);
      if (bulletMatch) {
        const [, indent, bullet, text] = bulletMatch;

        // If the line is empty (just the bullet), check if this is the second consecutive Enter
        if (text.trim() === "") {
          // Check if the last action was also creating an empty bullet item
          if (lastMarkdownActionRef.current === "empty-bullet-item") {
            // Second Enter on empty item - remove it and don't continue the list
            e.preventDefault();
            const newContent =
              beforeCursor.slice(0, -currentLine.length) + afterCursor;
            setContent(newContent);
            setTimeout(() => {
              textarea.selectionStart = start - currentLine.length;
              textarea.selectionEnd = start - currentLine.length;
            }, 0);
            // Reset the last action
            lastMarkdownActionRef.current = "";
            return;
          }
          // First Enter on empty item - create another empty item and track it
          lastMarkdownActionRef.current = "empty-bullet-item";
        } else {
          // Has text content, reset the tracking
          lastMarkdownActionRef.current = "";
        }

        // Continue the bullet list
        e.preventDefault();
        const newContent = `${beforeCursor}\n${indent}${bullet} ${afterCursor}`;
        setContent(newContent);
        setTimeout(() => {
          const newPos = start + indent.length + 3; // +3 for \n, bullet, and space
          textarea.selectionStart = newPos;
          textarea.selectionEnd = newPos;
        }, 0);
        return;
      }
    }

    // Cmd/Ctrl + B for bold
    if ((e.metaKey || e.ctrlKey) && e.key === "b") {
      e.preventDefault();
      const textarea = textareaRef.current;
      if (!textarea) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = content.substring(start, end);
      const before = content.substring(0, start);
      const after = content.substring(end);

      const newText = `${before}**${selectedText}**${after}`;
      setContent(newText);

      setTimeout(() => {
        textarea.selectionStart = start + 2;
        textarea.selectionEnd = end + 2;
        textarea.focus();
      }, 0);
    }

    // Cmd/Ctrl + I for italic
    if ((e.metaKey || e.ctrlKey) && e.key === "i") {
      e.preventDefault();
      const textarea = textareaRef.current;
      if (!textarea) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = content.substring(start, end);
      const before = content.substring(0, start);
      const after = content.substring(end);

      const newText = `${before}*${selectedText}*${after}`;
      setContent(newText);

      setTimeout(() => {
        textarea.selectionStart = start + 1;
        textarea.selectionEnd = end + 1;
        textarea.focus();
      }, 0);
    }

    // Cmd/Ctrl + K for inline code
    if ((e.metaKey || e.ctrlKey) && e.key === "k") {
      e.preventDefault();
      const textarea = textareaRef.current;
      if (!textarea) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = content.substring(start, end);
      const before = content.substring(0, start);
      const after = content.substring(end);

      const newText = `${before}\`${selectedText}\`${after}`;
      setContent(newText);

      setTimeout(() => {
        textarea.selectionStart = start + 1;
        textarea.selectionEnd = end + 1;
        textarea.focus();
      }, 0);
    }
  };

  // Helper function to get cursor coordinates from position
  const getCursorCoordinates = (
    position: number
  ): { top: number; left: number } | null => {
    const textarea = textareaRef.current;
    if (!textarea) return null;

    // Create a mirror div to calculate position
    const textBeforeCursor = content.substring(0, position);
    const lines = textBeforeCursor.split("\n");
    const lineNumber = lines.length - 1;
    const columnNumber = lines[lines.length - 1].length;

    // Get the computed style of the textarea
    const style = window.getComputedStyle(textarea);
    const lineHeight = parseFloat(style.lineHeight);
    const paddingTop = parseFloat(style.paddingTop);
    const paddingLeft = parseFloat(style.paddingLeft);

    // Calculate approximate position
    const fontSize = parseFloat(style.fontSize);
    const charWidth = fontSize * 0.6; // Approximate monospace character width

    // Calculate position in content coordinates
    const contentTop = paddingTop + lineNumber * lineHeight;
    const contentLeft = paddingLeft + columnNumber * charWidth;

    // Adjust for scroll position
    const displayTop = contentTop - scrollOffset.top;
    const displayLeft = contentLeft - scrollOffset.left;

    // Check if cursor is within visible viewport of textarea
    const isVisible =
      displayTop >= -30 && // -30 to account for the user label above cursor
      displayTop <= textarea.clientHeight &&
      displayLeft >= -50 && // Some padding for labels
      displayLeft <= textarea.clientWidth;

    if (!isVisible) {
      return null;
    }

    return {
      top: displayTop,
      left: displayLeft,
    };
  };

  return (
    <div className="min-h-screen bg-white text-black flex flex-col px-2 sm:px-4 py-2 sm:py-4 font-mono text-xs sm:text-sm relative">
      <div className="w-full max-w-6xl mx-auto">
        {/* ASCII Art Header - Desktop */}
        <pre className="hidden sm:block text-xs mb-4 select-none">
          {`┌─────────────────────────────────────────────────────────────────┐
│                      ★ PLAINTEXT NOTES ★                       │
│                 minimal • autosaving • shareable                │
└─────────────────────────────────────────────────────────────────┘`}
        </pre>

        {/* ASCII Art Header - Mobile */}
        <pre className="sm:hidden text-[0.6rem] mb-3 select-none">
          {`┌─────────────────────────────────┐
│     ★ PLAINTEXT NOTES ★        │
│  minimal • autosaving          │
└─────────────────────────────────┘`}
        </pre>

        {/* Action Buttons - Only show for authenticated users */}
        {isAuthenticated && (
          <div className="mb-3 sm:mb-4 flex flex-wrap gap-2 text-xs">
            {/* Mobile Drawer Toggle */}
            <button
              onClick={() => setIsDrawerOpen(!isDrawerOpen)}
              className="md:hidden cursor-pointer px-2 py-1 border border-black hover:bg-black hover:text-white transition-colors"
            >
              [ {isDrawerOpen ? "✕ CLOSE" : "☰ NOTES"} ({notes.length}) ]
            </button>

            <button
              onClick={handleNewNote}
              className="cursor-pointer px-2 sm:px-3 py-1 border border-black hover:bg-black hover:text-white transition-colors"
            >
              [ + NEW ]
            </button>
            <button
              onClick={() => setShowDeleteModal(true)}
              disabled={!note_id || isDeleting}
              className="cursor-pointer px-2 sm:px-3 py-1 border border-black hover:bg-black hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              [ {isDeleting ? "DELETING..." : "DELETE"} ]
            </button>
            <button
              onClick={() => loadNotes()}
              className="cursor-pointer px-2 sm:px-3 py-1 border border-black hover:bg-black hover:text-white transition-colors"
            >
              [ ⟳ REFRESH ]
            </button>
          </div>
        )}

        <div className="flex flex-col md:flex-row gap-3 md:gap-4 relative">
          {/* Sidebar with ASCII boxes - Desktop: always visible, Mobile: drawer - Only show for authenticated users */}
          {isAuthenticated && (
            <aside
              className={`
                md:w-80 text-xs
                md:block md:static md:h-auto
                fixed inset-0 z-40 bg-white
                transition-transform duration-300 ease-in-out
                ${
                  isDrawerOpen
                    ? "translate-x-0"
                    : "-translate-x-full md:translate-x-0"
                }
                overflow-y-auto p-4 md:p-0
              `}
            >
              {/* Mobile drawer close button */}
              <div className="md:hidden flex justify-between items-center mb-4 pb-2 border-b-2 border-black">
                <span className="font-bold text-sm">═══ NOTES LIST ═══</span>
                <button
                  onClick={() => setIsDrawerOpen(false)}
                  className="cursor-pointer px-3 py-1 border border-black hover:bg-black hover:text-white transition-colors text-xs"
                >
                  [ ✕ CLOSE ]
                </button>
              </div>

              {/* Desktop header */}
              <div className="hidden md:block mb-2">
                <span className="font-bold">═══ NOTES LIST ═══</span>
              </div>

              {isLoadingNotes && (
                <div className="mb-2 opacity-60">⟳ Loading notes...</div>
              )}

              {notesError && (
                <div className="mb-2 text-red-600">✗ {notesError}</div>
              )}

              {notes.length === 0 && !isLoadingNotes && !notesError && (
                <div className="mb-2 opacity-60">-- No notes yet --</div>
              )}

              <div className="space-y-2">
                {notes.map((n) => {
                  const isActive =
                    (note_id && n.id === note_id) ||
                    (!note_id && n.id === note?.id);
                  // Responsive title length
                  const titleLength = window.innerWidth < 640 ? 25 : 31;
                  const title = getTitle(n)
                    .substring(0, titleLength)
                    .padEnd(titleLength, " ");
                  const time = getShortTime(n.updated_at).padEnd(
                    titleLength,
                    " "
                  );

                  return (
                    <button
                      key={n.id}
                      onClick={() => {
                        navigate(`/notes/${n.id}`);
                        setIsDrawerOpen(false); // Close drawer when note selected
                      }}
                      className={`w-full text-left block border transition-colors cursor-pointer ${
                        isActive
                          ? "bg-[#fbf1c7] border-black"
                          : "border-black hover:bg-[#fbf1c7]"
                      }`}
                    >
                      {/* Desktop note box */}
                      <pre className="hidden sm:block p-2 text-[0.65rem]">
                        {`┌─────────────────────────────────┐
│ ${title} │
│ ${time} │
└─────────────────────────────────┘`}
                      </pre>
                      {/* Mobile note box */}
                      <pre className="sm:hidden p-2 text-[0.55rem]">
                        {`┌───────────────────────────┐
│ ${title} │
│ ${time} │
└───────────────────────────┘`}
                      </pre>
                    </button>
                  );
                })}
              </div>
            </aside>
          )}

          {/* Overlay for mobile drawer - Only show for authenticated users */}
          {isAuthenticated && isDrawerOpen && (
            <div
              className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
              onClick={() => setIsDrawerOpen(false)}
            />
          )}

          {/* Main Editor Area */}
          <main className="flex-1">
            {/* Note Title for Unauthenticated Users */}
            {!isAuthenticated && note && (
              <div className="mb-3">
                <h1 className="text-lg sm:text-xl font-bold border-b-2 border-black pb-2">
                  {note.title || "Untitled"}
                </h1>
              </div>
            )}

            {/* Status Bar - Only show for authenticated users */}
            <div className="mb-2 flex items-center justify-between text-[0.65rem] sm:text-xs border-b border-black pb-1">
              <div className="flex items-center gap-2">
                <span className="opacity-60">
                  {wsConnected ? (
                    <span className="text-green-600 mr-2">● WS Connected</span>
                  ) : (
                    <span className="text-red-600 mr-2">● WS Disconnected</span>
                  )}
                  {remoteCursors.size > 0 && (
                    <span className="text-blue-600 mr-2">
                      {remoteCursors.size}{" "}
                      {remoteCursors.size === 1
                        ? "collaborator"
                        : "collaborators"}
                    </span>
                  )}
                </span>
                {/* View Mode Toggle */}
                <div className="flex gap-1 ml-4">
                  <button
                    onClick={() => setViewMode("markdown")}
                    className={`px-2 py-0.5 border border-black transition-colors ${
                      viewMode === "markdown"
                        ? "bg-black text-white"
                        : "hover:bg-gray-100"
                    }`}
                    title="Markdown Mode"
                  >
                    MARKDOWN
                  </button>
                  <button
                    onClick={() => setViewMode("split")}
                    className={`px-2 py-0.5 border border-black transition-colors ${
                      viewMode === "split"
                        ? "bg-black text-white"
                        : "hover:bg-gray-100"
                    }`}
                    title="Split View"
                  >
                    SPLIT
                  </button>
                  <button
                    onClick={() => setViewMode("formatted")}
                    className={`px-2 py-0.5 border border-black transition-colors ${
                      viewMode === "formatted"
                        ? "bg-black text-white"
                        : "hover:bg-gray-100"
                    }`}
                    title="Formatted Mode"
                  >
                    FORMATTED
                  </button>
                </div>
              </div>
              <span className="opacity-60">
                {isSaving
                  ? "⟳ Saving..."
                  : lastSaved
                  ? `✓ Saved ${new Date(lastSaved).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}`
                  : "• Not yet saved"}
              </span>
            </div>
            {error && (
              <div className="mb-3 text-xs border-2 border-red-600 p-2 bg-red-50">
                <span className="font-bold">✗ ERROR:</span> {error}
              </div>
            )}

            {/* Main Editor Area with Split View */}
            <div className="border-2 border-black relative">
              {/* Markdown Toolbar - Show in all modes, behaves based on cursor position */}
              <div className="bg-gray-100 border-b border-black px-2 py-1.5 flex flex-wrap gap-1 text-[0.65rem]">
                <button
                  onMouseDown={(e) => {
                    // Prevent button from taking focus away from formatted view
                    e.preventDefault();
                  }}
                  onClick={() => {
                    // Check if selection is in formatted view
                    const isInFormatted = isSelectionInFormattedView();

                    if (isInFormatted) {
                      applyFormattingInFormattedView("strong");
                      return;
                    }

                    // Handle markdown view
                    const textarea = textareaRef.current;
                    if (!textarea) return;
                    const start = textarea.selectionStart;
                    const end = textarea.selectionEnd;
                    const selectedText =
                      content.substring(start, end) || "text";
                    const before = content.substring(0, start);
                    const after = content.substring(end);
                    setContent(`${before}**${selectedText}**${after}`);
                    setTimeout(() => {
                      textarea.focus();
                      textarea.selectionStart = start + 2;
                      textarea.selectionEnd = end + 2;
                    }, 0);
                  }}
                  className="px-2 py-0.5 border border-black hover:bg-black hover:text-white transition-colors"
                  title="Bold (Cmd/Ctrl+B)"
                >
                  <strong>B</strong>
                </button>
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    // Check if selection is in formatted view
                    if (isSelectionInFormattedView()) {
                      applyFormattingInFormattedView("em");
                      return;
                    }

                    // Handle markdown view
                    const textarea = textareaRef.current;
                    if (!textarea) return;
                    const start = textarea.selectionStart;
                    const end = textarea.selectionEnd;
                    const selectedText =
                      content.substring(start, end) || "text";
                    const before = content.substring(0, start);
                    const after = content.substring(end);
                    setContent(`${before}*${selectedText}*${after}`);
                    setTimeout(() => {
                      textarea.focus();
                      textarea.selectionStart = start + 1;
                      textarea.selectionEnd = end + 1;
                    }, 0);
                  }}
                  className="px-2 py-0.5 border border-black hover:bg-black hover:text-white transition-colors"
                  title="Italic (Cmd/Ctrl+I)"
                >
                  <em>I</em>
                </button>
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    // Check if selection is in formatted view
                    if (isSelectionInFormattedView()) {
                      applyFormattingInFormattedView("code");
                      return;
                    }

                    // Handle markdown view
                    const textarea = textareaRef.current;
                    if (!textarea) return;
                    const start = textarea.selectionStart;
                    const end = textarea.selectionEnd;
                    const selectedText =
                      content.substring(start, end) || "code";
                    const before = content.substring(0, start);
                    const after = content.substring(end);
                    setContent(`${before}\`${selectedText}\`${after}`);
                    setTimeout(() => {
                      textarea.focus();
                      textarea.selectionStart = start + 1;
                      textarea.selectionEnd = end + 1;
                    }, 0);
                  }}
                  className="px-2 py-0.5 border border-black hover:bg-black hover:text-white transition-colors font-mono"
                  title="Code (Cmd/Ctrl+K)"
                >
                  {"<>"}
                </button>
                <span className="border-l border-black mx-1"></span>
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    // Check if selection is in formatted view
                    if (isSelectionInFormattedView()) {
                      insertHeadingInFormattedView(1);
                      return;
                    }

                    // Handle markdown view
                    const textarea = textareaRef.current;
                    if (!textarea) return;
                    const start = textarea.selectionStart;
                    const lineStart = content.lastIndexOf("\n", start - 1) + 1;
                    const before = content.substring(0, lineStart);
                    const after = content.substring(lineStart);
                    setContent(`${before}# ${after}`);
                    setTimeout(() => {
                      textarea.focus();
                      textarea.selectionStart = start + 2;
                      textarea.selectionEnd = start + 2;
                    }, 0);
                  }}
                  className="px-2 py-0.5 border border-black hover:bg-black hover:text-white transition-colors"
                  title="Heading"
                >
                  H1
                </button>
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    const textarea = textareaRef.current;
                    const formattedDiv = formattedViewRef.current;

                    // Check if selection is in formatted view
                    if (isSelectionInFormattedView() && formattedDiv) {
                      const selection = window.getSelection();
                      if (!selection || !selection.rangeCount) return;

                      const range = selection.getRangeAt(0);

                      // Create new task list item
                      const ul = document.createElement("ul");
                      const li = document.createElement("li");
                      li.className = "task-list-item";

                      const checkbox = document.createElement("input");
                      checkbox.type = "checkbox";
                      checkbox.disabled = false;
                      checkbox.addEventListener("click", handleCheckboxClick);

                      // Create text node for content
                      const textSpan = document.createElement("span");
                      textSpan.appendChild(document.createTextNode(""));

                      li.appendChild(checkbox);
                      li.appendChild(textSpan);
                      ul.appendChild(li);

                      // Insert at cursor position
                      range.deleteContents();

                      // If we're at the end of content or in empty space, append
                      const currentNode = range.startContainer;
                      if (currentNode.nodeType === Node.TEXT_NODE) {
                        // Insert after the current text node's parent
                        const parent = currentNode.parentNode;
                        if (parent && parent.parentNode) {
                          parent.parentNode.insertBefore(
                            ul,
                            parent.nextSibling
                          );
                        }
                      } else if (currentNode.nodeType === Node.ELEMENT_NODE) {
                        // Insert at the position in the element
                        const element = currentNode as HTMLElement;
                        if (range.startOffset < element.childNodes.length) {
                          element.insertBefore(
                            ul,
                            element.childNodes[range.startOffset]
                          );
                        } else {
                          element.appendChild(ul);
                        }
                      } else {
                        // Fallback: append to formatted div
                        formattedDiv.appendChild(ul);
                      }

                      // Set cursor inside the new list item
                      setTimeout(() => {
                        const newRange = document.createRange();
                        const newSelection = window.getSelection();
                        if (textSpan.firstChild) {
                          newRange.setStart(textSpan.firstChild, 0);
                          newRange.collapse(true);
                          newSelection?.removeAllRanges();
                          newSelection?.addRange(newRange);
                        }
                        formattedDiv.focus();

                        // Sync to markdown
                        setTimeout(() => handleFormattedInput(), 10);
                      }, 0);

                      return;
                    }

                    // Handle markdown view (original behavior)
                    if (!textarea) return;
                    const start = textarea.selectionStart;
                    const lineStart = content.lastIndexOf("\n", start - 1) + 1;
                    const before = content.substring(0, lineStart);
                    const after = content.substring(lineStart);
                    setContent(`${before}- [ ] ${after}`);
                    setTimeout(() => {
                      textarea.focus();
                      textarea.selectionStart = start + 6;
                      textarea.selectionEnd = start + 6;
                    }, 0);
                  }}
                  className="px-2 py-0.5  border border-black hover:bg-black hover:text-white transition-colors"
                  title="Task List"
                >
                  Checkbox
                </button>
                <button
                  onClick={() => {
                    const textarea = textareaRef.current;
                    if (!textarea) return;
                    const start = textarea.selectionStart;
                    const end = textarea.selectionEnd;
                    const selectedText =
                      content.substring(start, end) || "link text";
                    const before = content.substring(0, start);
                    const after = content.substring(end);
                    setContent(`${before}[${selectedText}](url)${after}`);
                    setTimeout(() => {
                      textarea.focus();
                      textarea.selectionStart = start + selectedText.length + 3;
                      textarea.selectionEnd = start + selectedText.length + 6;
                    }, 0);
                  }}
                  className="px-2 py-0.5 border border-black hover:bg-black hover:text-white transition-colors"
                  title="Link"
                >
                  🔗
                </button>
                <button
                  onClick={() => {
                    const textarea = textareaRef.current;
                    if (!textarea) return;
                    const start = textarea.selectionStart;
                    const before = content.substring(0, start);
                    const after = content.substring(start);
                    setContent(
                      `${before}\n\`\`\`\ncode block\n\`\`\`\n${after}`
                    );
                    setTimeout(() => {
                      textarea.focus();
                      textarea.selectionStart = start + 5;
                      textarea.selectionEnd = start + 15;
                    }, 0);
                  }}
                  className="px-2 py-0.5 border border-black hover:bg-black hover:text-white transition-colors"
                  title="Code Block"
                >
                  {"{ }"}
                </button>
                <button
                  onClick={() => {
                    const textarea = textareaRef.current;
                    if (!textarea) return;
                    const start = textarea.selectionStart;
                    const before = content.substring(0, start);
                    const after = content.substring(start);
                    setContent(
                      `${before}\n| Header 1 | Header 2 |\n|----------|----------|\n| Cell 1   | Cell 2   |\n${after}`
                    );
                    setTimeout(() => {
                      textarea.focus();
                      textarea.selectionStart = start + 3;
                      textarea.selectionEnd = start + 11;
                    }, 0);
                  }}
                  className="px-2 py-0.5 border border-black hover:bg-black hover:text-white transition-colors"
                  title="Table"
                >
                  ⊞
                </button>
                <span className="border-l border-black mx-1"></span>
                <button
                  onClick={() => setShowMarkdownHelp(!showMarkdownHelp)}
                  className="px-2 py-0.5 border border-black hover:bg-black hover:text-white transition-colors"
                  title="Markdown Help"
                >
                  ?
                </button>
              </div>

              {/* Markdown Help Overlay */}
              {showMarkdownHelp && (
                <div className="absolute top-0 left-0 right-0 bg-yellow-50 border-b-2 border-black z-10 p-3 text-[0.65rem] max-h-60 overflow-y-auto">
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-bold text-sm">
                      ═══ MARKDOWN QUICK REFERENCE ═══
                    </span>
                    <button
                      onClick={() => setShowMarkdownHelp(false)}
                      className="px-2 py-0.5 border border-black hover:bg-black hover:text-white transition-colors"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <div className="font-bold mb-1">Text Formatting:</div>
                      <code className="block bg-white p-1 mb-1 border border-gray-300">
                        **bold** or __bold__
                      </code>
                      <code className="block bg-white p-1 mb-1 border border-gray-300">
                        *italic* or _italic_
                      </code>
                      <code className="block bg-white p-1 mb-1 border border-gray-300">
                        `inline code`
                      </code>
                      <code className="block bg-white p-1 mb-1 border border-gray-300">
                        ~~strikethrough~~
                      </code>
                    </div>
                    <div>
                      <div className="font-bold mb-1">Headings:</div>
                      <code className="block bg-white p-1 mb-1 border border-gray-300">
                        # Heading 1
                      </code>
                      <code className="block bg-white p-1 mb-1 border border-gray-300">
                        ## Heading 2
                      </code>
                      <code className="block bg-white p-1 mb-1 border border-gray-300">
                        ### Heading 3
                      </code>
                    </div>
                    <div>
                      <div className="font-bold mb-1">Lists:</div>
                      <code className="block bg-white p-1 mb-1 border border-gray-300">
                        - Bullet item
                      </code>
                      <code className="block bg-white p-1 mb-1 border border-gray-300">
                        1. Numbered item
                      </code>
                      <code className="block bg-white p-1 mb-1 border border-gray-300">
                        - [ ] Task (unchecked)
                      </code>
                      <code className="block bg-white p-1 mb-1 border border-gray-300">
                        - [x] Task (checked)
                      </code>
                    </div>
                    <div>
                      <div className="font-bold mb-1">Links & More:</div>
                      <code className="block bg-white p-1 mb-1 border border-gray-300">
                        [text](url)
                      </code>
                      <code className="block bg-white p-1 mb-1 border border-gray-300">
                        &gt; Blockquote
                      </code>
                      <code className="block bg-white p-1 mb-1 border border-gray-300">
                        --- (horizontal rule)
                      </code>
                    </div>
                    <div className="md:col-span-2">
                      <div className="font-bold mb-1">Code Block:</div>
                      <code className="block bg-white p-1 border border-gray-300">
                        ```language{"\n"}code here{"\n"}```
                      </code>
                    </div>
                    <div className="md:col-span-2">
                      <div className="font-bold mb-1">Keyboard Shortcuts:</div>
                      <span className="inline-block bg-white px-1 border border-gray-300 mr-2">
                        Cmd/Ctrl+B
                      </span>{" "}
                      Bold
                      <span className="inline-block bg-white px-1 border border-gray-300 mr-2 ml-3">
                        Cmd/Ctrl+I
                      </span>{" "}
                      Italic
                      <span className="inline-block bg-white px-1 border border-gray-300 mr-2 ml-3">
                        Cmd/Ctrl+K
                      </span>{" "}
                      Code
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-col md:flex-row h-[60vh]">
                {/* Markdown Pane */}
                {(viewMode === "markdown" || viewMode === "split") && (
                  <div
                    className={`${
                      viewMode === "split"
                        ? "md:w-1/2 md:border-r-2 border-black"
                        : "w-full"
                    } ${
                      viewMode === "split" ? "h-1/2 md:h-full" : "h-full"
                    } relative`}
                  >
                    <div className="bg-gray-50 border-b border-black px-2 py-1 text-[0.65rem] font-bold sticky top-0 z-10">
                      ═══ MARKDOWN ═══
                    </div>
                    <div className="relative w-full h-[calc(100%-2rem)]">
                      <textarea
                        ref={textareaRef}
                        value={content}
                        onChange={(e) => {
                          setContent(e.target.value);
                          handleCursorMove();
                        }}
                        onKeyDown={handleKeyDown}
                        onMouseUp={handleCursorMove}
                        onKeyUp={handleCursorMove}
                        onClick={handleCursorMove}
                        onSelect={handleCursorMove}
                        placeholder={
                          "Start typing with Markdown...\n\n# Heading 1\n## Heading 2\n\n**bold** (Cmd/Ctrl+B) *italic* (Cmd/Ctrl+I) `code` (Cmd/Ctrl+K)\n\n- [ ] Task list\n- [x] Completed task\n\n```javascript\ncode block\n```\n\n[Links](https://example.com)\n\n> Blockquotes\n\n| Tables | Are | Supported |\n|--------|-----|----------|\n| cell   | cell| cell     |"
                        }
                        className="w-full h-full resize-none p-4 text-xs sm:text-sm leading-relaxed focus:outline-none  font-mono border-none transition-all placeholder:text-gray-400 placeholder:italic"
                        style={{
                          textShadow: "0 0 1px rgba(0,0,0,0.05)",
                        }}
                        spellCheck={false}
                      />

                      {/* Remote Cursors - Only show cursors from users in markdown view */}
                      {Array.from(remoteCursors.entries())
                        .filter(([, cursor]) => cursor.view_mode === "markdown")
                        .map(([userId, cursor]) => {
                          const coords = getCursorCoordinates(cursor.position);

                          if (!coords) {
                            return null;
                          }

                          return (
                            <div
                              key={userId}
                              className="absolute pointer-events-none z-10"
                              style={{
                                top: `${coords.top}px`,
                                left: `${coords.left}px`,
                              }}
                            >
                              {/* Cursor line */}
                              <div
                                className="absolute w-0.5 h-5 animate-pulse"
                                style={{
                                  backgroundColor: cursor.color,
                                }}
                              />
                              {/* User label */}
                              <div
                                className="absolute -top-5 left-0 text-[0.6rem] px-1 py-0.5 rounded whitespace-nowrap font-sans"
                                style={{
                                  backgroundColor: cursor.color,
                                  color: "white",
                                }}
                              >
                                {cursor.user_name}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}

                {/* Formatted Pane (Notion-like editable) */}
                {(viewMode === "formatted" || viewMode === "split") && (
                  <div
                    className={`${
                      viewMode === "split" ? "md:w-1/2" : "w-full"
                    } ${
                      viewMode === "split"
                        ? "h-1/2 md:h-full border-t-2 md:border-t-0 border-black"
                        : "h-full"
                    } overflow-y-auto relative`}
                  >
                    <div className="bg-gray-50 border-b border-black px-2 py-1 text-[0.65rem] font-bold sticky top-0 z-10">
                      ═══ FORMATTED ═══
                    </div>
                    <div className="relative">
                      <div
                        key={formattedKey}
                        ref={formattedViewRef}
                        contentEditable={true}
                        suppressContentEditableWarning={true}
                        onInput={handleFormattedInput}
                        onKeyDown={handleFormattedKeyDown}
                        onKeyUp={handleFormattedCursorMove}
                        onClick={handleFormattedCursorMove}
                        onMouseUp={handleFormattedCursorMove}
                        onSelect={handleFormattedCursorMove}
                        className="p-3 prose prose-sm max-w-none markdown-preview formatted-editor focus:outline-none cursor-text"
                        style={{ minHeight: "calc(100% - 2rem)" }}
                        dangerouslySetInnerHTML={{
                          __html:
                            formattedHTML ||
                            "<p><em>No content yet. Start typing...</em></p>",
                        }}
                      />

                      {/* Remote Cursors in Formatted View - Only show cursors from users in formatted view */}
                      {Array.from(remoteCursors.entries())
                        .filter(
                          ([, cursor]) => cursor.view_mode === "formatted"
                        )
                        .map(([userId, cursor]) => {
                          if (!formattedViewRef.current) return null;

                          try {
                            // Use the position directly - it's already in formatted view coordinates
                            const formattedPosition = cursor.position;

                            // Build text nodes array
                            const walker = document.createTreeWalker(
                              formattedViewRef.current,
                              NodeFilter.SHOW_TEXT,
                              null
                            );

                            const textNodes: { node: Node; text: string }[] =
                              [];
                            while (walker.nextNode()) {
                              const node = walker.currentNode;
                              const text = node.textContent || "";
                              textNodes.push({ node, text });
                            }

                            // Find the text node containing the formatted position
                            let currentPos = 0;
                            let targetNode: Node | null = null;
                            let offsetInNode = 0;

                            for (const { node, text } of textNodes) {
                              if (
                                currentPos + text.length >=
                                formattedPosition
                              ) {
                                targetNode = node;
                                offsetInNode = Math.min(
                                  formattedPosition - currentPos,
                                  text.length
                                );
                                break;
                              }
                              currentPos += text.length;
                            }

                            if (!targetNode) {
                              return null;
                            }

                            // Create a range at this position
                            const range = document.createRange();
                            range.setStart(targetNode, offsetInNode);
                            range.setEnd(targetNode, offsetInNode);

                            // Get the bounding rectangle
                            const rect = range.getBoundingClientRect();
                            const containerRect =
                              formattedViewRef.current.getBoundingClientRect();

                            // Get the parent scrollable container
                            const scrollContainer =
                              formattedViewRef.current.parentElement;
                            const scrollTop = scrollContainer?.scrollTop || 0;
                            const scrollLeft = scrollContainer?.scrollLeft || 0;

                            // Calculate position relative to the container, accounting for scroll
                            const top =
                              rect.top - containerRect.top + scrollTop;
                            const left =
                              rect.left - containerRect.left + scrollLeft;

                            return (
                              <div
                                key={userId}
                                className="absolute pointer-events-none z-10"
                                style={{
                                  top: `${top}px`,
                                  left: `${left}px`,
                                }}
                              >
                                {/* Cursor line */}
                                <div
                                  className="absolute w-0.5 h-5 animate-pulse"
                                  style={{
                                    backgroundColor: cursor.color,
                                  }}
                                />
                                {/* User label */}
                                <div
                                  className="absolute -top-5 left-0 text-[0.6rem] px-1 py-0.5 rounded whitespace-nowrap font-sans"
                                  style={{
                                    backgroundColor: cursor.color,
                                    color: "white",
                                  }}
                                >
                                  {cursor.user_name}
                                </div>
                              </div>
                            );
                          } catch (error) {
                            console.error(
                              "Error rendering formatted cursor:",
                              error
                            );
                            return null;
                          }
                        })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Share URL Box */}
            <div className="mt-3 text-[0.6rem] sm:text-xs">
              {/* Desktop Share URL */}
              <pre className="hidden sm:block border border-black p-2 bg-gray-50 overflow-x-auto">
                {`┌─ SHARE URL ────────────────────────────────────────────────────────┐
│ ${shareUrl.padEnd(57, " ")}   │ `}
                <button
                  onClick={handleCopyUrl}
                  className="cursor-pointer hover:opacity-60 transition-opacity inline"
                  title="Copy URL"
                >
                  {copySuccess ? "Copied" : "Copy"}
                </button>
                {` │
└───────────────────────────────────────────────────────────────────┘`}
              </pre>

              {/* Mobile Share URL */}
              <div className="sm:hidden border border-black p-2 bg-gray-50">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-bold">SHARE URL:</span>
                  <button
                    onClick={handleCopyUrl}
                    className="cursor-pointer px-2 py-1 border border-black hover:bg-black hover:text-white transition-colors"
                    title="Copy URL"
                  >
                    {copySuccess ? "✓ COPIED" : "⎘ COPY"}
                  </button>
                </div>
                <div className="mt-1 break-all text-[0.55rem] opacity-70">
                  {shareUrl}
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-white bg-opacity-40 flex items-center justify-center p-2 sm:p-4 z-50">
          <div className="bg-white border-4 border-black font-mono max-w-md w-full shadow-2xl mx-2">
            {/* Desktop Modal Header */}
            <pre className="hidden sm:block p-4 text-xs border-b-2 border-black bg-[#fbf1c7]">
              {`╔═══════════════════════════════════════╗
║          ⚠ CONFIRM DELETE ⚠           ║
╚═══════════════════════════════════════╝`}
            </pre>

            {/* Mobile Modal Header */}
            <div className="sm:hidden p-3 text-xs border-b-2 border-black bg-[#fbf1c7] text-center font-bold">
              ⚠ CONFIRM DELETE ⚠
            </div>

            <div className="p-4 sm:p-6 text-xs sm:text-sm space-y-3 sm:space-y-4">
              <p className="leading-relaxed">
                Are you sure you want to delete this note?
              </p>
              <p className="text-xs font-bold">
                ⚠ This action cannot be undone!
              </p>
            </div>

            <div className="border-t-2 border-black p-3 sm:p-4 flex gap-2 justify-end text-xs">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="cursor-pointer px-3 sm:px-4 py-2 border-2 border-black hover:bg-[#fbf1c7] transition-colors"
                disabled={isDeleting}
              >
                [ CANCEL ]
              </button>
              <button
                onClick={handleDeleteCurrent}
                className="cursor-pointer px-3 sm:px-4 py-2 border-2 border-black bg-black text-white hover:bg-red-600 hover:border-red-600 transition-colors"
                disabled={isDeleting}
              >
                [ {isDeleting ? "DELETING..." : "DELETE"} ]
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotesPage;
